import type { VisitorProfileField } from "@/lib/graphql/queries/generated/profile.generated";
import { BooleanControl } from "./boolean-control";
import { DateControl } from "./date-control";
import { NumberControl } from "./number-control";
import { OptionsControl } from "./options-control";
import { StringControl } from "./string-control";

/**
 * Dispatch by `field.type` to the matching control. AVATAR returns
 * `null` here — it's rendered out-of-band by the avatar hero at the
 * top of the form, not as a row in the field list.
 */
export function FieldControl({
  id,
  field,
  value,
  onChange,
  t,
}: {
  id: string;
  field: VisitorProfileField;
  value: string | null;
  onChange: (next: string | null) => void;
  /** Translator threaded down so `OptionsControl` can localise known
   *  system-enum values (e.g. `chatStatus.IN_PROGRESS`). String / number
   *  / date / boolean controls don't need it. */
  t: (key: string) => string;
}) {
  const disabled = field.pinned;
  switch (field.type) {
    case "BOOLEAN":
      return <BooleanControl id={id} value={value} disabled={disabled} onChange={onChange} />;
    case "NUMBER":
      return <NumberControl id={id} value={value} disabled={disabled} onChange={onChange} />;
    case "DATE":
      return <DateControl id={id} value={value} disabled={disabled} onChange={onChange} />;
    case "ENUM":
      return (
        <OptionsControl
          id={id}
          fieldKey={field.key}
          mode="single"
          options={field.options ?? []}
          value={value}
          disabled={disabled}
          onChange={onChange}
          t={t}
        />
      );
    case "ARRAY":
      return (
        <OptionsControl
          id={id}
          fieldKey={field.key}
          mode="multi"
          options={field.options ?? []}
          value={value}
          disabled={disabled}
          onChange={onChange}
          t={t}
        />
      );
    case "AVATAR":
      // Rendered by `AvatarHero` at the top of the form.
      return null;
    default:
      return (
        <StringControl
          id={id}
          fieldKey={field.key}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      );
  }
}
